import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import WorkOrderForm from "./WorkOrderForm";

/**
 * Modal for approving a service request by creating a work order.
 * Pre-fills the WO form from the service request data.
 *
 * Props:
 *   isOpen           — boolean
 *   toggle           — function to close the modal
 *   serviceRequest   — the request being approved
 *   onApprove        — (payload) => void
 *   isSubmitting     — boolean
 *   apiError         — string
 *   locations        — location options array
 *   enumData         — dynamic enums array
 *   technicians      — technician options array
 *   categories       — category options array
 *   vendors          — vendor options array
 *   organizationId   — for asset search API
 */
const ApproveServiceRequestModal = ({
  isOpen,
  toggle,
  serviceRequest,
  onApprove,
  isSubmitting = false,
  apiError,
  locations = [],
  enumData = [],
  technicians = [],
  categories = [],
  vendors = [],
  organizationId,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // Pre-fill form from service request when modal opens
  useEffect(() => {
    if (isOpen && serviceRequest) {
      // Convert formatted urgencyLevel back to raw enum value
      const toRawEnum = (val) => {
        if (!val) return null;
        return val.toUpperCase().replace(/ /g, '_');
      };

      setFormData({
        title: serviceRequest.title || '',
        assetId: serviceRequest.asset?.id || null,
        asset: serviceRequest.asset || null,
        locationId: serviceRequest.locationId || null,
        description: serviceRequest.description || '',
        urgencyLevel: toRawEnum(serviceRequest.urgencyLevel),
        usageStatus: serviceRequest.asset?.assetStatus || '',
        imageUrls: serviceRequest.imageUrls || [],
        documentUrls: serviceRequest.documentUrls || [],
        workType: 'REACTIVE',
        assignedTechnician: [],
        categoryIds: [],
        vendorIds: [],
        firstOccurrence: null,
        requestDate: null,
        estimatedTime: '',
        recurrenceType: 'NONE',
        recurrenceDays: [],
        recurrenceInterval: 1,
        recurrenceDayOfMonth: 1,
        occurrences: null,
        dueDate: null,
        recurrenceStartDate: null,
        recurrenceEndDate: null,
      });
      setErrors({});
    }
  }, [isOpen, serviceRequest]);

  const handleChange = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    // Clear errors for changed fields
    const clearedErrors = { ...errors };
    Object.keys(updates).forEach(key => {
      if (clearedErrors[key]) delete clearedErrors[key];
    });
    setErrors(clearedErrors);
  };

  const handleSubmit = () => {
    // Validate
    const newErrors = {};
    if (!formData.title?.trim()) {
      newErrors.title = t('business.components.validation.title_required', 'Title is required');
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Build payload
    const payload = {
      serviceRequestId: serviceRequest?.id,
      title: formData.title,
      assetId: formData.assetId || null,
      locationId: formData.locationId || null,
      description: formData.description || null,
      urgencyLevel: formData.urgencyLevel || null,
      usageStatus: formData.usageStatus || null,
      downtimeReasonId: formData.downtimeReasonId || null,
      imageUrls: formData.imageUrls || [],
      documentUrls: formData.documentUrls || [],
      workType: formData.workType || 'REACTIVE',
      assignedTechnician: formData.assignedTechnician?.map(t => t.id) || [],
      categoryIds: formData.categoryIds || [],
      vendorIds: formData.vendorIds || [],
      firstOccurrence: formData.firstOccurrence || null,
      requestDate: formData.requestDate || null,
      estimatedTime: formData.estimatedTime
        ? convertHoursToTimeFormat(parseFloat(formData.estimatedTime))
        : null,
      recurrenceType: formData.recurrenceType || null,
      recurrenceDays: formData.recurrenceDays || null,
      recurrenceInterval: formData.recurrenceInterval || null,
      recurrenceDayOfMonth: formData.recurrenceDayOfMonth || null,
      occurrences: formData.occurrences || null,
      dueDate: formData.dueDate || null,
      recurrenceStartDate: formData.recurrenceStartDate || null,
      recurrenceEndDate: formData.recurrenceEndDate || null,
    };

    onApprove(payload);
  };

  // Helper: convert decimal hours to HH:MM:SS.mmm format
  const convertHoursToTimeFormat = (hours) => {
    if (!hours || isNaN(hours)) return null;
    const totalHours = Math.floor(hours);
    const minutes = Math.floor((hours - totalHours) * 60);
    const seconds = Math.floor(((hours - totalHours) * 60 - minutes) * 60);
    const milliseconds = Math.floor((((hours - totalHours) * 60 - minutes) * 60 - seconds) * 1000);
    return `${totalHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="modal-header text-white">
          <div>
            <h5 className="modal-title mb-0 fw-bold">
              {t('business.components.approve_modal.title', 'Approve Request — Create Work Order')}
            </h5>
            <p className="text-white-50 small mb-0">
              {serviceRequest?.requestNumber}
            </p>
          </div>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={toggle}
            aria-label={t('shared.close', 'Close')}
          />
        </div>
        <div className="modal-body p-4">
          <WorkOrderForm
            formData={formData}
            onChange={handleChange}
            errors={errors}
            apiError={apiError}
            locations={locations}
            technicians={technicians}
            categories={categories}
            vendors={vendors}
            enumData={enumData}
            isEditMode={false}
            organizationId={organizationId}
          />
        </div>
        <div className="modal-footer bg-light border-top-0">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={toggle}
            disabled={isSubmitting}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('shared.processing', 'Approving...')}
              </>
            ) : (
              t('business.components.approve_modal.button_approve', 'Approve & Create Work Order')
            )}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default ApproveServiceRequestModal;
